import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { PartnerCenterEnrollmentStatus } from "@gamedev-web/one-gaming-crm";

import { PartnerPageLayout } from "../../components/PartnerPageLayout";
import { PageLoadingSpinner } from "../../components/PageLoadingSpinner";
import { ScreenReaderHeading } from "../../components/ScreenReaderHeading";
import {
  appDeveloperAgreement,
  enabledQueryParameterValue,
  partnerQueryParameters,
  xboxOnboardSiteLocationPrefix,
} from "../../constants";
import { useRequireAuth } from "../../constants/urlsClientSide";
import {
  PartnerAccessLevel,
  PartnerAccountRequirement,
  usePartnerRouteGuard,
} from "../../helpers/usePartnerRouteGuard";
import {
  clearNdaResendCooldown,
  getNdaResendAvailableAt,
  startInitialNdaResendCooldown,
  startNdaResendCooldown,
} from "../../helpers/ndaResendCooldown";
import { useString, useStrings } from "../../localization/localizationHelpers";
import { xboxOnboardActions } from "../../redux/actions/xboxOnboardActions";
import {
  getXboxOnboardData,
  getXboxOnboardIsLoading,
} from "../../redux/selectors/xboxOnboardSelectors";
import { XboxOnboardApplicationStatus } from "../../server/crm/api/models/xboxOnboardModels";
import type { XboxOnboardEnrollmentEvidence } from "../../server/crm/api/models/xboxOnboardModels";
import { EmbargoedEnrollmentModal } from "../PartnerHomePage/components/EmbargoedEnrollmentModal";
import * as styles from "./PartnerLegalPage.module.css";

const AgreementsLinkPlaceholder = "__AGREEMENTS_LINK__";
const SettingsLinkPlaceholder = "__PARTNER_SETTINGS_LINK__";

const PartnerLegalPage = () => {
  const isPageReady = usePartnerRouteGuard(
    PartnerAccessLevel.NdaPending,
    PartnerAccountRequirement.NonAaa
  );
  const isSignedIn = useRequireAuth();
  const dispatch = useDispatch();
  const data = useSelector(getXboxOnboardData);
  const isLoading = useSelector(getXboxOnboardIsLoading);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isResendingNda, setIsResendingNda] = useState(false);
  const [isResendCooldownLoaded, setIsResendCooldownLoaded] = useState(false);
  const [ndaResendAvailableAt, setNdaResendAvailableAt] = useState<number | null>(null);
  const [hasNdaResendError, setHasNdaResendError] = useState(false);
  const [enrollmentModalAction, setEnrollmentModalAction] = useState<"start" | "retry">();
  const userEmail = data?.contact?.email || "";
  const ndaSentDate = data?.ndaSentDate
    ? new Date(data.ndaSentDate).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";
  const ndaSignedDate = data?.ndaSignedDate
    ? new Date(data.ndaSignedDate).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";
  const enrollmentDate = data?.enrollmentDate
    ? new Date(data.enrollmentDate).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";
  const [
    agreementsLabel,
    partnerLegalUnsignedAgreementsTitle,
    partnerLegalResendButton,
    partnerLegalNdaResendError,
    partnerLegalAppDisclosureAgreementTitle,
    partnerLegalAppDisclosureAgreementDescription,
    termsCheckboxPrefix,
    appDeveloperAgreementLink,
    enrollButton,
    processingButton,
    retryButton,
    partnerLegalSignedAgreementsTitle,
    noDateFallback,
    settingsLabel,
  ] = useStrings(
    "agreementsLabel",
    "partnerLegalUnsignedAgreementsTitle",
    "partnerLegalResendButton",
    "partnerLegalNdaResendError",
    "partnerLegalAppDisclosureAgreementTitle",
    "partnerLegalAppDisclosureAgreementDescription",
    "idXboxGdkTermsCheckboxPrefix",
    "appDeveloperAgreementLink",
    "idXboxGdkEnroll",
    "processingButton",
    "partnerHomeRetryButton",
    "partnerLegalSignedAgreementsTitle",
    "ticketTeamsAdminNoManagers",
    "partnerSettingsPageTitle"
  );
  const enrollmentRejectedGuidance = useString(
    "partnerEnrollmentRejectedGuidance",
    SettingsLinkPlaceholder
  );
  const [enrollmentRejectedGuidanceStart, enrollmentRejectedGuidanceEnd = ""] =
    enrollmentRejectedGuidance.split(SettingsLinkPlaceholder);
  const ndaSentMessage = useString(
    data?.ndaSentDate ? "partnerLegalNdaSentWithDateMessage" : "partnerLegalNdaSentMessage",
    userEmail,
    ndaSentDate
  );
  const ndaTitle = useString(
    data?.ndaNumber
      ? "partnerLegalNonDisclosureAgreementNumberTitle"
      : "partnerLegalNonDisclosureAgreementTitle",
    data?.ndaNumber ?? ""
  );
  const ndaProcessingNotice = useString(
    "partnerLegalNdaProcessingNotice",
    AgreementsLinkPlaceholder
  );
  const signedDate = useString("infoBoxNDASignedDate", ndaSignedDate || noDateFallback);
  const enrolledDate = useString("partnerLegalEnrolledDate", enrollmentDate);
  const [ndaProcessingNoticeStart, ndaProcessingNoticeEnd = ""] =
    ndaProcessingNotice.split(AgreementsLinkPlaceholder);
  const isNdaResendCooldownActive = ndaResendAvailableAt !== null;

  const handleResendNda = async () => {
    setIsResendingNda(true);
    setHasNdaResendError(false);

    try {
      const response = await fetch(`${xboxOnboardSiteLocationPrefix}/resendNda`, {
        method: "POST",
      });
      if (!response.ok) {
        throw new Error(`NDA resend failed: ${response.status}`);
      }

      const availableAt = startNdaResendCooldown(window.localStorage, userEmail);
      setNdaResendAvailableAt(availableAt);
      window.location.href = `${xboxOnboardSiteLocationPrefix}/dashboard?${partnerQueryParameters.welcome}=${enabledQueryParameterValue}`;
    } catch {
      setHasNdaResendError(true);
      setIsResendingNda(false);
    }
  };

  useEffect(() => {
    if (isSignedIn && !data) {
      dispatch(xboxOnboardActions.fetchData.start());
    }
  }, [dispatch, isSignedIn, data]);

  useEffect(() => {
    if (!userEmail) {
      return;
    }

    const availableAt = data?.ndaSentDate
      ? startInitialNdaResendCooldown(window.localStorage, userEmail)
      : getNdaResendAvailableAt(window.localStorage, userEmail);
    setNdaResendAvailableAt(availableAt);
    setIsResendCooldownLoaded(true);
  }, [data?.ndaSentDate, userEmail]);

  useEffect(() => {
    if (!userEmail || ndaResendAvailableAt === null) {
      return;
    }

    const remainingMs = ndaResendAvailableAt - Date.now();
    if (remainingMs <= 0) {
      clearNdaResendCooldown(window.localStorage, userEmail);
      setNdaResendAvailableAt(null);
      return;
    }

    const timeout = window.setTimeout(() => {
      clearNdaResendCooldown(window.localStorage, userEmail);
      setNdaResendAvailableAt(null);
    }, remainingMs);

    return () => window.clearTimeout(timeout);
  }, [ndaResendAvailableAt, userEmail]);

  if (!isPageReady || !isSignedIn || (isLoading && !data)) {
    return (
      <PartnerPageLayout breadcrumbLabel={agreementsLabel}>
        <PageLoadingSpinner />
      </PartnerPageLayout>
    );
  }

  const isNdaSigned = data?.status === XboxOnboardApplicationStatus.NdaSigned;
  const enrollmentStatus = data?.partnerCenterEnrollmentStatus;
  const isEnrollmentComplete =
    enrollmentStatus === PartnerCenterEnrollmentStatus.Complete ||
    enrollmentStatus === PartnerCenterEnrollmentStatus.EarlyComplete;
  const isEnrollmentInProgress =
    enrollmentStatus === PartnerCenterEnrollmentStatus.InProgress ||
    enrollmentStatus === PartnerCenterEnrollmentStatus.EarlyInProgress;
  const isEnrollmentRejected =
    enrollmentStatus === PartnerCenterEnrollmentStatus.Rejected ||
    enrollmentStatus === PartnerCenterEnrollmentStatus.EarlyRejected;
  const canSubmitPCEnrollment = data?.hubPermissions.canSubmitPCEnrollment === true;

  const handleEnrollmentAction = (action: "start" | "retry") => {
    if (data?.isEmbargoedCountry) {
      setEnrollmentModalAction(action);
      return;
    }

    dispatch(
      action === "start"
        ? xboxOnboardActions.startEnrollment.start()
        : xboxOnboardActions.retryEnrollment.start()
    );
  };

  const handleEmbargoedEnrollment = (evidence: XboxOnboardEnrollmentEvidence) => {
    dispatch(
      enrollmentModalAction === "retry"
        ? xboxOnboardActions.retryEnrollment.start(evidence)
        : xboxOnboardActions.startEnrollment.start(evidence)
    );
    setEnrollmentModalAction(undefined);
  };

  const hasUnsignedAgreements = !isNdaSigned || (isNdaSigned && !isEnrollmentComplete);

  return (
    <>
      <EmbargoedEnrollmentModal
        isOpen={Boolean(enrollmentModalAction)}
        initialDunsValue={data?.dunsNumber}
        onClose={() => setEnrollmentModalAction(undefined)}
        onSubmit={handleEmbargoedEnrollment}
      />
      <PartnerPageLayout breadcrumbLabel={agreementsLabel}>
        <ScreenReaderHeading>{agreementsLabel}</ScreenReaderHeading>
        <div className={styles.content}>
          <div className={styles.mainColumn}>
            {hasUnsignedAgreements && (
              <>
                <h2 className={styles.sectionTitle}>{partnerLegalUnsignedAgreementsTitle}</h2>

                {!isNdaSigned && (
                  <div className={styles.agreementCard}>
                    <div className={styles.agreementContent}>
                      <h3 className={styles.agreementTitle}>{ndaTitle}</h3>
                      <p className={styles.agreementDescription}>
                        {ndaSentMessage}
                        <br />
                        <br />
                        {ndaProcessingNoticeStart}
                        <Link
                          to={`${xboxOnboardSiteLocationPrefix}/agreements`}
                          className={styles.supportLink}
                        >
                          {agreementsLabel}
                        </Link>
                        {ndaProcessingNoticeEnd}
                      </p>
                    </div>
                    {data?.canResendNda && (
                      <div className={styles.resendActions}>
                        <button
                          className={styles.resendButton}
                          onClick={handleResendNda}
                          disabled={
                            isResendingNda || !isResendCooldownLoaded || isNdaResendCooldownActive
                          }
                        >
                          {partnerLegalResendButton}
                        </button>
                        {hasNdaResendError && (
                          <p className={styles.errorMessage} role="alert">
                            {partnerLegalNdaResendError}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {isNdaSigned && !isEnrollmentComplete ? (
                  <div className={styles.agreementCard}>
                    <div className={styles.agreementContent}>
                      <h3 className={styles.agreementTitle}>
                        {partnerLegalAppDisclosureAgreementTitle}
                      </h3>
                      <p className={styles.agreementDescription}>
                        {partnerLegalAppDisclosureAgreementDescription}
                      </p>
                      {canSubmitPCEnrollment &&
                        (enrollmentStatus === PartnerCenterEnrollmentStatus.Enabled ||
                          enrollmentStatus === PartnerCenterEnrollmentStatus.Unset) && (
                          <div className={styles.enrollmentActions}>
                            <label className={styles.termsCheckboxContainer}>
                              <input
                                type="checkbox"
                                checked={termsAccepted}
                                onChange={() => setTermsAccepted(!termsAccepted)}
                                className={styles.checkbox}
                              />
                              <span className={styles.termsLabel}>
                                {termsCheckboxPrefix}{" "}
                                <a
                                  href={appDeveloperAgreement}
                                  className={styles.agreementActionLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {appDeveloperAgreementLink}
                                </a>
                              </span>
                            </label>
                            <button
                              className={styles.enrollButton}
                              disabled={!termsAccepted}
                              onClick={() => handleEnrollmentAction("start")}
                            >
                              {enrollButton}
                            </button>
                          </div>
                        )}
                      {canSubmitPCEnrollment && isEnrollmentInProgress && (
                        <div className={styles.enrollmentActions} role="status" aria-live="polite">
                          <label className={styles.termsCheckboxContainer}>
                            <input
                              type="checkbox"
                              checked={true}
                              disabled={true}
                              className={styles.checkbox}
                            />
                            <span className={styles.termsLabel}>
                              {termsCheckboxPrefix}{" "}
                              <a
                                href={appDeveloperAgreement}
                                className={styles.agreementActionLink}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {appDeveloperAgreementLink}
                              </a>
                            </span>
                          </label>
                          <button className={styles.processingButton} disabled={true}>
                            <span className={styles.processingSpinner} aria-hidden="true" />
                            {processingButton}
                          </button>
                        </div>
                      )}
                      {isEnrollmentRejected && (
                        <>
                          <p className={styles.errorMessage} role="alert">
                            {enrollmentRejectedGuidanceStart}
                            <Link
                              className={styles.rejectedSettingsLink}
                              to={`${xboxOnboardSiteLocationPrefix}/settings`}
                            >
                              {settingsLabel}
                            </Link>
                            {enrollmentRejectedGuidanceEnd}
                          </p>
                          {canSubmitPCEnrollment && (
                            <div className={styles.retryActions}>
                              <button
                                className={styles.enrollButton}
                                disabled={isLoading}
                                onClick={() => handleEnrollmentAction("retry")}
                              >
                                {isLoading ? processingButton : retryButton}
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ) : null}
              </>
            )}

            {isNdaSigned && (
              <>
                <h2 className={styles.sectionTitle}>{partnerLegalSignedAgreementsTitle}</h2>
                <div className={styles.agreementCard}>
                  <div className={styles.agreementContent}>
                    <h3 className={styles.agreementTitle}>{ndaTitle}</h3>
                    <p className={styles.signedDate}>{signedDate}</p>
                  </div>
                </div>
                {isEnrollmentComplete && (
                  <div className={styles.agreementCard}>
                    <div className={styles.agreementContent}>
                      <h3 className={styles.agreementTitle}>
                        {partnerLegalAppDisclosureAgreementTitle}
                      </h3>
                      {data?.enrollmentDate && <p className={styles.signedDate}>{enrolledDate}</p>}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <div />
        </div>
      </PartnerPageLayout>
    </>
  );
};

export default PartnerLegalPage;
